import type {
  IdentityChangeEntry,
  IdentityChangeSourceName,
  TextKey,
  Translated,
} from "@repo/shared/types";
import { Fingerprint, UserPen } from "lucide-react";
import type { DashboardTarget } from "@/lib/api/dashboard";
import { useDashboardWidget } from "@/lib/hooks/use-dashboard";
import { useT } from "@/lib/i18n/use-t";
import { useDashboardFormat } from "../use-dashboard-format";
import { T } from "./identity-changes-widget.i18n";
import { ListRow } from "./list-row";
import { WidgetCard } from "./widget-card";
import { WidgetEmpty } from "./widget-empty";

type Label = TextKey<Translated<typeof T>>;

const FIELD: Record<IdentityChangeEntry["field"], Label> = {
  name: "fieldName",
  ssn: "fieldSsn",
  country: "fieldCountry",
  dateOfBirth: "fieldDateOfBirth",
  orgId: "fieldOrgId",
};

const SOURCE: Record<IdentityChangeSourceName, Label> = {
  client_editor: "sourceClientEditor",
  trapets_lookup: "sourceTrapetsLookup",
  sumsub_internal: "sourceSumsubInternal",
  sumsub_public: "sourceSumsubPublic",
  eid_login: "sourceEidLogin",
  flow_completion: "sourceFlowCompletion",
  public_api: "sourcePublicApi",
};

export function IdentityChangesWidget({ target }: { target: DashboardTarget }) {
  const { data, isLoading, error } = useDashboardWidget("identity_changes", target);
  const t = useT(T);
  const { relative } = useDashboardFormat();

  return (
    <WidgetCard
      icon={Fingerprint}
      title={t.identityChanges}
      description={t.identityChangesDescription}
      badge={data ? t.latest(data.items.length) : undefined}
      footer={data ? { label: t.openIdentityChanges, to: data.link } : undefined}
      isLoading={isLoading}
      error={error}
    >
      {data &&
        (data.items.length === 0 ? (
          <WidgetEmpty>{t.noIdentityChanges}</WidgetEmpty>
        ) : (
          <div className="space-y-3">
            {data.items.map((change) => (
              <ListRow
                key={change.id}
                icon={UserPen}
                // The national ID and org number are the hard identifiers; flag those amber.
                tone={change.field === "ssn" || change.field === "orgId" ? "warning" : "info"}
                title={change.clientName ?? "—"}
                detail={`${t.changed(t[FIELD[change.field]])} · ${t[SOURCE[change.source]]}`}
                meta={[change.changedByName, relative(change.changedAt)]
                  .filter(Boolean)
                  .join(" · ")}
                to={`/clients/${change.clientId}`}
              />
            ))}
          </div>
        ))}
    </WidgetCard>
  );
}
